import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Snackbar,
  LinearProgress,
  Paper,
 } from '@mui/material';
 import forensicService from '../services/forensicService';
 import ClientSelector from '../components/ClientSelector';
 import { useAppStore } from '../store/appStore';
 
 interface AuditStatus {
   task_id: string;
   status: string;
   progress: number;
   details: string | null;
 }
 
 const AuditPage: React.FC = () => {
   const [selectedClient, setSelectedClient] = useState<string | null>(null);
   const [error, setError] = useState<string | null>(null);
   const [successMessage, setSuccessMessage] = useState<string | null>(null);
   const [auditStatus, setAuditStatus] = useState<AuditStatus | null>(null);
   const setIsAuditRunning = useAppStore((state) => state.setIsAuditRunning);
   const wsRef = React.useRef<WebSocket | null>(null);
 
   const isAuditRunning = auditStatus?.status === 'running' || auditStatus?.status === 'pending' || auditStatus?.status === 'queued' || auditStatus?.status === 'in_progress';
 
   useEffect(() => {
     setIsAuditRunning(isAuditRunning);
   }, [isAuditRunning, setIsAuditRunning]);
 
   const connectWebSocket = (taskId: string) => {
     if (wsRef.current) {
       wsRef.current.close();
     }
 
     const wsUrl = `${process.env.REACT_APP_API_URL!.replace(/^http/, 'ws')}/forensic/ws/audit-status/${taskId}`;
     const ws = new WebSocket(wsUrl);
     wsRef.current = ws;
 
     ws.onopen = () => {
       console.log('WebSocket connected');
     };
 
     ws.onmessage = (event) => {
       const data = JSON.parse(event.data);
       setAuditStatus(data);
     };
 
     ws.onerror = (event) => {
       console.error('WebSocket error:', event);
       setError('Error de conexión con el servidor de progreso.');
     };
 
     ws.onclose = () => {
       console.log('WebSocket disconnected');
       if (auditStatus?.status !== 'completed' && auditStatus?.status !== 'failed') {
         setSuccessMessage('La conexión con el servidor de progreso ha finalizado.');
       }
     };
   };
 
   useEffect(() => {
     return () => {
       if (wsRef.current) {
         wsRef.current.close();
       }
       setIsAuditRunning(false);
     };
   }, [setIsAuditRunning]);
 
   const handleClientChange = (clientName: string | null) => {
     setSelectedClient(clientName);
     setAuditStatus(null);
     if (wsRef.current) {
       wsRef.current.close();
     }
   };
 
   const handleStartAudit = async () => {
     if (!selectedClient) {
       setError('Por favor, seleccione un cliente.');
       return;
     }
 
     try {
       setError(null);
       setSuccessMessage(null);
       const response = await forensicService.startAudit(selectedClient);
       const initialStatus = response.data;
       setAuditStatus(initialStatus);
       connectWebSocket(initialStatus.task_id);
     } catch (err: any) {
       setError(err.response?.data?.detail || 'Error al iniciar la auditoría.');
       console.error(err);
     }
   };
   
   const translateStatus = (status: string) => {
     switch (status) {
       case 'running':
       case 'in_progress':
         return 'En progreso';
       case 'pending':
         return 'Pendiente';
       case 'queued':
         return 'En cola';
       case 'completed':
         return 'Completado';
       case 'failed':
         return 'Falló';
       default:
         return status;
     }
   };
 
   const handleExport = async () => {
     if (!selectedClient) {
      setError('Por favor, seleccione un cliente para exportar.');
      return;
    }
    try {
      setError(null);
      setSuccessMessage('Exportando datos...');
      const response = await forensicService.exportAudit(selectedClient);
      
      // Crear un enlace para descargar el archivo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let filename = `reporte_${selectedClient}.xlsx`;
      if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch.length > 1) {
              filename = filenameMatch[1];
          }
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSuccessMessage('Datos exportados con éxito.');

    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al exportar los datos.');
      console.error(err);
    }
  };

  return (
    <>
      <Typography variant="h4" gutterBottom>
        Módulo de Auditoría Forense
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <ClientSelector selectedClient={selectedClient} onClientChange={handleClientChange} />

      <Paper sx={{ mt: 2, p: 2, height: 'calc(100vh - 270px)', overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 4 }}>
          <Button
            variant="outlined"
            color="secondary"
          onClick={handleExport}
          disabled={!selectedClient || isAuditRunning}
          size="large"
          sx={{ textTransform: 'none' }}
        >
          Exportar a Excel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleStartAudit}
          disabled={!selectedClient || isAuditRunning}
          size="large"
          sx={{ fontWeight: 500, textTransform: 'none' }}
        >
          {isAuditRunning ? 'Auditoría en Progreso...' : 'Iniciar Auditoría'}
          </Button>
        </Box>

        {auditStatus && (
          <Box>
            <Typography variant="h6">Estado de la Auditoría</Typography>
            <Typography>ID de Tarea: {auditStatus.task_id}</Typography>
            <Typography>Estado: {translateStatus(auditStatus.status)}</Typography>
            <Typography>Detalles: {auditStatus.details === 'No new documents to audit.' ? 'No se encontraron documentos para procesar.' : (auditStatus.details || 'N/A')}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress variant="determinate" value={auditStatus.progress * 100} />
              </Box>
              <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">{`${Math.round(auditStatus.progress * 100)}%`}</Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>
      
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </>
  );
};

export default AuditPage;